import React, { useState, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import styled, { keyframes } from 'styled-components';
import { kioskStore } from '~/stores/kiosk.store';

/**
 * KioskKeyboard
 *
 * Teclado numérico que aparece quando o usuário aciona o "7-click" no canto
 * superior esquerdo e há PINs cadastrados no servidor (KIOSK_PASSWORD_PLAN Fase 3).
 *
 * Comportamento:
 * - 4-6 dígitos. Auto-valida ao atingir 6.
 * - Auto-fecha após {@link AUTO_CLOSE_MS} sem interação.
 * - Suporta D-pad (Tizen/webOS): setas + Enter pra ativar tecla com foco.
 * - Touch (Android) e mouse/teclado físico (browser/dev) também funcionam.
 * - Em lockout, mostra countdown e desabilita input.
 */

const AUTO_CLOSE_MS = 15_000;
const PIN_MIN_LENGTH = 4;
const PIN_MAX_LENGTH = 6;

interface KeyboardProps {
  /**
   * Disparado quando: usuário aperta Esc/RETURN/BACK, OU auto-close de 15s
   * sem interação. NÃO é chamado no Backdrop click — em touchscreen, o
   * click sintético do toque que abriu o teclado cairia no Backdrop e
   * fecharia imediatamente (race condition).
   */
  onClose: () => void;
  onUnlocked: () => void;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.18s ease-out;
`;

const Panel = styled.div`
  background: #15233b;
  border-radius: 16px;
  padding: 32px 28px;
  min-width: 360px;
  box-shadow: 0 16px 64px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  user-select: none;
`;

const Title = styled.div`
  color: #f1f5f9;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dots = styled.div`
  display: flex;
  gap: 10px;
  margin: 4px 0 8px 0;
  min-height: 24px;
`;

const Dot = styled.div<{ $filled: boolean; $error?: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $filled, $error }) =>
    $error ? '#dc2626' : $filled ? '#60a5fa' : 'transparent'};
  border: 2px solid ${({ $error }) => ($error ? '#dc2626' : '#475569')};
  transition:
    background-color 0.15s,
    border-color 0.15s;
`;

const Pad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const Key = styled.button<{ $focused: boolean; $danger?: boolean }>`
  width: 80px;
  height: 70px;
  border-radius: 10px;
  border: 2px solid ${({ $focused }) => ($focused ? '#60a5fa' : '#334155')};
  background: ${({ $focused, $danger }) =>
    $focused ? '#1d4ed8' : $danger ? '#7f1d1d' : '#1e293b'};
  color: #f1f5f9;
  font-size: 24px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition:
    background-color 0.1s,
    border-color 0.1s,
    transform 0.05s;

  &:active {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StatusLine = styled.div<{ $error?: boolean }>`
  color: ${({ $error }) => ($error ? '#fca5a5' : '#94a3b8')};
  font-size: 13px;
  min-height: 18px;
  text-align: center;
`;

// Layout do teclado — 12 keys: 1..9, ←, 0, ✓
type KeyDef = {
  label: string;
  value: string;
  kind: 'digit' | 'backspace' | 'submit';
};
const KEYS: KeyDef[] = [
  { label: '1', value: '1', kind: 'digit' },
  { label: '2', value: '2', kind: 'digit' },
  { label: '3', value: '3', kind: 'digit' },
  { label: '4', value: '4', kind: 'digit' },
  { label: '5', value: '5', kind: 'digit' },
  { label: '6', value: '6', kind: 'digit' },
  { label: '7', value: '7', kind: 'digit' },
  { label: '8', value: '8', kind: 'digit' },
  { label: '9', value: '9', kind: 'digit' },
  { label: '⌫', value: 'BACK', kind: 'backspace' },
  { label: '0', value: '0', kind: 'digit' },
  { label: '✓', value: 'OK', kind: 'submit' },
];

/** Layout 3-col, índices das colunas vizinhas pra navegação D-pad. */
const COLS = 3;

export const KioskKeyboard: React.FC<KeyboardProps> = observer(
  ({ onClose, onUnlocked }) => {
    const [pin, setPin] = useState('');
    const [feedbackError, setFeedbackError] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [focusedIdx, setFocusedIdx] = useState(10); // foca '0' por default
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Timers do pós-submit. Guardados em ref pra serem cancelados no unmount:
    // sem isso, o componente desmontado (unlock concluído / teardown de teste)
    // ainda dispara `onUnlocked`/`setFeedbackError` depois de morto.
    const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const errorFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const validatingRef = useRef(false);

    const remainingLockoutSec = kioskStore.remainingLockoutSec;
    const isLocked = remainingLockoutSec > 0;

    const restartCloseTimer = useCallback(() => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => {
        console.log('[KioskKeyboard] Auto-close after 15s idle');
        onClose();
      }, AUTO_CLOSE_MS);
    }, [onClose]);

    const handleSubmit = useCallback(
      async (currentPin: string) => {
        if (validatingRef.current) return;
        if (currentPin.length < PIN_MIN_LENGTH) {
          setStatusMessage(`Mínimo ${PIN_MIN_LENGTH} dígitos`);
          return;
        }
        validatingRef.current = true;
        setStatusMessage('Verificando...');
        try {
          const result = await kioskStore.attemptUnlockWithPin(currentPin);
          if (result.success) {
            setStatusMessage('Desbloqueado');
            unlockTimerRef.current = setTimeout(onUnlocked, 200);
            return;
          }
          setFeedbackError(true);
          setPin('');
          if (result.locked) {
            setStatusMessage(`Bloqueado por ${result.remainingLockoutSec}s`);
          } else {
            setStatusMessage('PIN incorreto');
          }
          if (errorFeedbackTimerRef.current) {
            clearTimeout(errorFeedbackTimerRef.current);
          }
          errorFeedbackTimerRef.current = setTimeout(
            () => setFeedbackError(false),
            600,
          );
        } finally {
          validatingRef.current = false;
        }
      },
      [onUnlocked],
    );

    const press = useCallback(
      (keyDef: KeyDef) => {
        restartCloseTimer();
        setStatusMessage(null);

        if (isLocked) {
          setStatusMessage(`Aguarde ${remainingLockoutSec}s`);
          return;
        }

        if (keyDef.kind === 'backspace') {
          setPin((p) => p.slice(0, -1));
          return;
        }
        if (keyDef.kind === 'submit') {
          handleSubmit(pin);
          return;
        }
        // digit
        setPin((p) => {
          if (p.length >= PIN_MAX_LENGTH) return p;
          const next = p + keyDef.value;
          // Auto-submit ao atingir o tamanho máximo
          if (next.length === PIN_MAX_LENGTH) {
            queueMicrotask(() => handleSubmit(next));
          }
          return next;
        });
      },
      [isLocked, remainingLockoutSec, pin, handleSubmit, restartCloseTimer],
    );

    // Cleanup dos timers do pós-submit no unmount. Efeito separado (deps [])
    // — o efeito do D-pad abaixo re-roda a cada mudança de `press`/`focusedIdx`
    // e cancelaria o timer de unlock no meio do caminho.
    useEffect(
      () => () => {
        if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
        if (errorFeedbackTimerRef.current) {
          clearTimeout(errorFeedbackTimerRef.current);
        }
      },
      [],
    );

    // D-pad / keyboard físico
    useEffect(() => {
      const handleKey = (e: KeyboardEvent) => {
        restartCloseTimer();

        // Esc fecha
        if (
          e.key === 'Escape' ||
          e.keyCode === 10009 /* Tizen RETURN */ ||
          e.keyCode === 461 /* webOS BACK */
        ) {
          e.preventDefault();
          onClose();
          return;
        }

        // Dígitos físicos (atalho útil em browser/dev e teclado USB)
        if (/^[0-9]$/.test(e.key)) {
          e.preventDefault();
          const idx = KEYS.findIndex((k) => k.value === e.key);
          if (idx !== -1) press(KEYS[idx]);
          return;
        }
        if (e.key === 'Backspace') {
          e.preventDefault();
          press(KEYS[9]); // backspace
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          // Enter ativa a tecla focada (D-pad style)
          press(KEYS[focusedIdx]);
          return;
        }

        // D-pad nav
        const move = (delta: number) => {
          e.preventDefault();
          setFocusedIdx((idx) => {
            const next = idx + delta;
            if (next < 0 || next >= KEYS.length) return idx;
            return next;
          });
        };
        switch (e.key) {
          case 'ArrowLeft':
            move(-1);
            break;
          case 'ArrowRight':
            move(+1);
            break;
          case 'ArrowUp':
            move(-COLS);
            break;
          case 'ArrowDown':
            move(+COLS);
            break;
        }
      };

      // Capture no window, igual ao blocker do kiosk Tizen (lockTizenKioskKeys).
      // O blocker chama stopPropagation na captura do window: um listener em
      // bubble nunca receberia a tecla e o PIN ficava impossível de digitar pelo
      // controle. No MESMO nó os dois listeners rodam, com ou sem stopPropagation.
      window.addEventListener('keydown', handleKey, true);
      restartCloseTimer();
      return () => {
        window.removeEventListener('keydown', handleKey, true);
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      };
    }, [press, focusedIdx, onClose, restartCloseTimer]);

    return (
      // Backdrop NÃO fecha ao clicar — em mobile, o click sintético do toque
      // que abriu o teclado cai aqui ~300ms depois e fechava imediatamente.
      // Pra fechar: Esc/RETURN/BACK key, ou aguardar auto-close de 15s.
      <Backdrop>
        <Panel onClick={(e) => e.stopPropagation()}>
          <Title>🔒 Kiosk</Title>

          <Dots>
            {Array.from({ length: PIN_MAX_LENGTH }).map((_, i) => (
              <Dot key={i} $filled={i < pin.length} $error={feedbackError} />
            ))}
          </Dots>

          <Pad>
            {KEYS.map((k, idx) => (
              <Key
                key={k.value}
                $focused={idx === focusedIdx}
                $danger={k.kind === 'backspace'}
                disabled={isLocked && k.kind !== 'backspace'}
                onClick={() => {
                  setFocusedIdx(idx);
                  press(k);
                }}
              >
                {k.label}
              </Key>
            ))}
          </Pad>

          <StatusLine $error={feedbackError || isLocked}>
            {isLocked
              ? `Bloqueado por ${remainingLockoutSec}s`
              : (statusMessage ?? `${pin.length}/${PIN_MAX_LENGTH}`)}
          </StatusLine>
        </Panel>
      </Backdrop>
    );
  },
);

export default KioskKeyboard;
